import { useRef, memo } from 'react';
import { NavLink, Link as RouterLink } from 'react-router-dom';
import { Transition } from 'react-transition-group';
import classNames from 'classnames';

// components
import Monogram from 'components/Monogram';
import Icon from 'components/Icon';
import { tokens } from 'components/ThemeProvider/theme';

import NavToggle from './NavToggle';
import ThemeToggle from './ThemeToggle';

// utils
import { useWindowSize, useAppContext } from 'hooks';
import { navLinks, socialLinks } from './navData';
import { reflow } from 'utils/transition';
import { media, msToNum, numToMs } from 'utils/style';
import { blurOnMouseUp } from 'utils/focus';

import './index.css';

const NavbarIcons = () => (
  <div className="navbar__nav-icons">
    {socialLinks.map(({ label, url, icon }) => (
      <a
        key={label}
        className="navbar__nav-icon-link"
        aria-label={label}
        href={url}
        onMouseUp={blurOnMouseUp}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Icon className="navbar__nav-icon" icon={icon} />
      </a>
    ))}
  </div>
);

function Header(props) {
  const { menuOpen, dispatch } = useAppContext();
  const { location } = props;
  const windowSize = useWindowSize();
  const headerRef = useRef();
  const isMobile = windowSize.width <= media.mobile || windowSize.height <= 696;

  const handleMobileNavClick = () => {
    if (menuOpen) dispatch({ type: 'toggleMenu' });
  };

  // Custom function to check if a nav link should be active
  const isNavLinkActive = navItem => {
    const { pathname, hash } = navItem;

    // For contact page, use simple pathname matching
    if (pathname === '/contact') {
      return location.pathname === '/contact';
    }

    // For home page with hash, match exact hash
    if (pathname === '/' && hash) {
      return location.pathname === '/' && location.hash === hash;
    }

    // For home page without hash, match only if no hash is present
    if (pathname === '/' && !hash) {
      return location.pathname === '/' && !location.hash;
    }

    return false;
  };

  return (
    <header className="navbar" ref={headerRef}>
      <RouterLink
        className="navbar__logo"
        to="/#intro"
        aria-label="Hashan Jayalath, Developer"
        onClick={handleMobileNavClick}
        onMouseUp={blurOnMouseUp}
      >
        <Monogram highlight />
      </RouterLink>
      <div className="navbar__mobile-controls">
        {isMobile && <ThemeToggle isMobile />}
        <NavToggle onClick={() => dispatch({ type: 'toggleMenu' })} menuOpen={menuOpen} />
      </div>
      <nav className="navbar__nav">
        <div className="navbar__nav-list">
          {navLinks.map(({ label, pathname, hash }) => (
            <NavLink
              className={() =>
                `navbar__nav-link ${
                  isNavLinkActive({ pathname, hash }) ? 'navbar__nav-link--active' : ''
                }`
              }
              key={label}
              to={`${pathname}${hash || ''}`}
              onMouseUp={blurOnMouseUp}
            >
              {label}
            </NavLink>
          ))}
        </div>
        <NavbarIcons />
      </nav>
      <Transition in={menuOpen} mountOnEnter unmountOnExit timeout={512} onEnter={reflow}>
        {status => (
          <nav
            className={classNames('navbar__mobile-nav', `navbar__mobile-nav--${status}`)}
          >
            {navLinks.map(({ label, pathname, hash }, index) => (
              <NavLink
                className={() =>
                  classNames(
                    'navbar__mobile-nav-link',
                    `navbar__mobile-nav-link--${status}`,
                    {
                      'navbar__mobile-nav-link--active': isNavLinkActive({
                        pathname,
                        hash,
                      }),
                    }
                  )
                }
                key={label}
                onClick={handleMobileNavClick}
                to={`${pathname}${hash || ''}`}
                onMouseUp={blurOnMouseUp}
                style={{
                  transitionDelay: numToMs(
                    Number(msToNum(tokens.base.durationS)) + index * 50
                  ),
                }}
              >
                {label}
              </NavLink>
            ))}
            <NavbarIcons />
          </nav>
        )}
      </Transition>
      {!isMobile && <ThemeToggle />}
    </header>
  );
}

export default memo(Header);
